<template>
  <div>
    <b-row class="mb-1">
      <b-col class="d-flex justify-content-between align-items-center flex-wrap">
        <div>
          <b-link
            class="d-block small mb-25"
            :to="{ name: 'notificationSettings' }"
          >
            <feather-icon icon="ArrowLeftIcon" />
            Volver a notificaciones
          </b-link>
          <h1 class="font-weight-bolder mb-0">
            {{ eventName }}
          </h1>
          <small class="text-muted">
            Canal: {{ channelLabel }}
          </small>
        </div>
        <b-button
          variant="outline-primary"
          :disabled="exporting || !summary.total"
          @click="exportCsv"
        >
          <feather-icon icon="DownloadIcon" />
          Exportar CSV
        </b-button>
      </b-col>
    </b-row>

    <b-row class="mb-1">
      <b-col md="3">
        <b-card class="mb-1">
          <h4 class="mb-0">
            {{ summary.total }}
          </h4>
          <small class="text-muted">Total</small>
        </b-card>
      </b-col>
      <b-col md="3">
        <b-card class="mb-1">
          <h4 class="mb-0 text-success">
            {{ summary.sent }}
          </h4>
          <small class="text-muted">Enviadas</small>
        </b-card>
      </b-col>
      <b-col md="3">
        <b-card class="mb-1">
          <h4 class="mb-0 text-danger">
            {{ summary.failed }}
          </h4>
          <small class="text-muted">Fallidas</small>
        </b-card>
      </b-col>
      <b-col md="3">
        <b-card class="mb-1">
          <h4 class="mb-0 text-warning">
            {{ summary.skipped }}
          </h4>
          <small class="text-muted">Omitidas</small>
        </b-card>
      </b-col>
    </b-row>

    <b-card class="mb-1">
      <b-row>
        <b-col md="3">
          <b-form-group label="Estado">
            <b-form-select
              v-model="filters.status"
              :options="statusOptions"
              @change="reload"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group label="Destinatario">
            <b-form-input
              v-model.trim="filters.search"
              placeholder="Nombre, correo o teléfono"
              @keyup.enter="reload"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Desde">
            <b-form-input
              v-model="filters.from"
              type="date"
              @change="reload"
            />
          </b-form-group>
        </b-col>
        <b-col md="2">
          <b-form-group label="Hasta">
            <b-form-input
              v-model="filters.to"
              type="date"
              @change="reload"
            />
          </b-form-group>
        </b-col>
        <b-col
          md="2"
          class="d-flex align-items-end"
        >
          <b-button
            block
            variant="outline-primary"
            class="mb-1"
            @click="reload"
          >
            Filtrar
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-overlay :show="loading">
      <b-card>
        <b-table
          :items="deliveries"
          :fields="fields"
          responsive
          small
          show-empty
          empty-text="Todavía no se ha enviado ninguna notificación de este tipo"
        >
          <template #cell(sent_at)="row">
            {{ formatDateTime(row.item.sent_at) }}
          </template>

          <template #cell(recipient_name)="row">
            <div class="font-weight-bolder">
              {{ row.item.recipient_name || 'Sin nombre' }}
            </div>
            <small class="text-muted">{{ row.item.recipient_address }}</small>
          </template>

          <template #cell(channel_label)="row">
            <b-badge variant="light-secondary">
              {{ row.item.channel_label }}
            </b-badge>
          </template>

          <template #cell(status)="row">
            <b-badge :variant="statusVariant(row.item.status)">
              {{ statusLabel(row.item.status) }}
            </b-badge>
            <div
              v-if="row.item.error_message"
              class="small mt-25"
              :class="row.item.status === 'failed' ? 'text-danger' : 'text-muted'"
            >
              {{ row.item.error_message }}
            </div>
          </template>

          <template #cell(body)="row">
            <div
              v-if="row.item.body"
              class="notification-body small"
            >
              {{ row.item.body }}
            </div>
            <span
              v-else
              class="text-muted"
            >—</span>
          </template>
        </b-table>

        <div class="d-flex justify-content-between align-items-center flex-wrap mt-1">
          <small class="text-muted">
            {{ pagination.total_objects }} registros
          </small>
          <b-pagination
            v-model="page"
            :total-rows="pagination.total_objects"
            :per-page="pagination.per_page"
            class="mb-0"
            @change="onPageChange"
          />
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BButton, BBadge, BLink, BOverlay, BTable, BPagination,
  BFormGroup, BFormInput, BFormSelect,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'
import moment from 'moment'

const ALL_CHANNELS = 'todos'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BBadge,
    BLink,
    BOverlay,
    BTable,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
  },

  data() {
    return {
      loading: false,
      exporting: false,
      page: 1,
      filters: {
        status: null,
        search: '',
        from: null,
        to: null,
      },
      statusOptions: [
        { value: null, text: 'Todos' },
        { value: 'sent', text: 'Enviadas' },
        { value: 'failed', text: 'Fallidas' },
        { value: 'skipped', text: 'Omitidas' },
      ],
    }
  },

  computed: {
    ...mapGetters('notifications', {
      deliveries: 'notificationDeliveries',
      summary: 'notificationDeliveriesSummary',
      pagination: 'notificationDeliveriesPagination',
      categories: 'notificationCategories',
      channels: 'notificationChannels',
    }),

    eventKey() {
      return this.$route.params.eventKey
    },

    channel() {
      return this.$route.params.channel
    },

    catalogEvent() {
      let found = null
      this.categories.forEach(category => {
        category.events.forEach(event => {
          if (event.key === this.eventKey) found = event
        })
      })
      return found
    },

    eventName() {
      return this.catalogEvent ? this.catalogEvent.name : this.eventKey
    },

    channelLabel() {
      if (this.channel === ALL_CHANNELS) return 'Todos'
      const channel = this.channels.find(item => item.key === this.channel)
      return channel ? channel.label : this.channel
    },

    fields() {
      const fields = [
        { key: 'sent_at', label: 'Fecha' },
        { key: 'recipient_name', label: 'Destinatario' },
      ]
      if (this.channel === ALL_CHANNELS) {
        fields.push({ key: 'channel_label', label: 'Canal' })
      }
      fields.push({ key: 'status', label: 'Estado' })
      fields.push({ key: 'body', label: 'Mensaje' })
      return fields
    },

    requestParams() {
      const params = {
        event_key: this.eventKey,
        page: this.page,
        perPage: 25,
      }
      if (this.channel !== ALL_CHANNELS) params.channel = this.channel
      if (this.filters.status) params.status = this.filters.status
      if (this.filters.search) params.search = this.filters.search
      if (this.filters.from) params.from = this.filters.from
      if (this.filters.to) params.to = this.filters.to
      return params
    },
  },

  created() {
    if (!this.categories.length) {
      this.$store.dispatch('notifications/fetchNotificationCatalog')
    }
    this.loadDeliveries()
  },

  methods: {
    async loadDeliveries() {
      this.loading = true
      try {
        await this.$store.dispatch('notifications/fetchNotificationDeliveries', this.requestParams)
      } finally {
        this.loading = false
      }
    },

    reload() {
      this.page = 1
      this.loadDeliveries()
    },

    onPageChange(page) {
      this.page = page
      this.$nextTick(this.loadDeliveries)
    },

    formatDateTime(value) {
      if (!value) return '—'
      return moment(value).format('DD/MMM/YYYY HH:mm')
    },

    statusLabel(status) {
      if (status === 'sent') return 'Enviada'
      if (status === 'failed') return 'Falló'
      return 'Omitida'
    },

    statusVariant(status) {
      if (status === 'sent') return 'light-success'
      if (status === 'failed') return 'light-danger'
      return 'light-warning'
    },

    async exportCsv() {
      this.exporting = true
      try {
        const blob = await this.$store.dispatch('notifications/exportNotificationDeliveries', this.requestParams)
        const url = window.URL.createObjectURL(new Blob([blob]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `notificaciones-${this.eventKey}-${this.channel}.csv`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } finally {
        this.exporting = false
      }
    },
  },
}
</script>

<style scoped>
.notification-body {
  max-width: 420px;
  max-height: 88px;
  overflow: auto;
  white-space: pre-wrap;
}
</style>
